import React from 'react';
import { css } from 'glamor';
import {graphql} from "@apollo/client/react/hoc";

import compose from 'lodash/flowRight';
import { configureForm } from '@thinkmill/pragmatic-forms';
import { Alert } from '../../../components/Alert';
import { LoadingFork } from '../../../components/LoadingFork';
import { withInstallation } from '../../../components/InstallationContext';
import { createSanctionedPlateQuery, sanctionedPlateContactsQuery } from './queries';
import VipContactPicker from '../../Concierge/dashboard/components/VipContactPicker';
import { useUi } from '../../Concierge/ui';
import Field from '../../Concierge/vip/components/Field';
import { T } from '../../Concierge/constants';

const validatePlate = ({ plate }) => {
	const errors = {};
	const disallowedInput = /[^a-z0-9 -]/gi;
	if (!plate.trim().length) {
		errors.plate = 'Please enter a plate';
	} else if (disallowedInput.test(plate)) {
		errors.plate = 'Plate should only include letters and numbers';
	}
	return errors;
};

export const CreateForm = compose(
	withInstallation,
	graphql(createSanctionedPlateQuery),
	configureForm({
		initFields: () => ({
			plate: '',
			label: '',
			lotId: null,
			isEnabled: true,
			isVip: false,
			vipContactIds: [],
		}),
		validate: validatePlate,
		submit: ({ plate, label, lotId, isVip, vipContactIds }, props) => {
			// Do some manipulations on the data going in.
			plate = plate.replace(/[^a-z0-9]/gi, '').toUpperCase();
			return props.mutate({
				variables: {
					input: {
						installationId: props.installation.id,
						plate,
						label,
						lotId: lotId || null,
						isVip,
						vipContactIds: isVip ? vipContactIds : [],
					},
				},
			});
		},
		onSuccess: (result, props, form) => {
			props.onCreateSuccess(result?.data?.createSanctionedPlate);
			form.reset();
		},
	}),
	graphql(sanctionedPlateContactsQuery, {
		name: 'contacts',
		options: (props) => ({ variables: { installationId: props.installation.id } }),
	}),
)(({ form, lotsOptions, contacts }) => {
	const ui = useUi();
	const isVipProps = form.getFieldProps({ type: 'checkbox', name: 'isVip' });
	const vipContactIdsProps = form.getFieldProps({ name: 'vipContactIds', type: 'text' });
	const lotProps = form.getFieldProps({ name: 'lotId', type: 'select' });
	return (
	<form.Form>
		{form.submitError && <Alert flavour="danger">{form.submitError.message}</Alert>}
		<div
			className={css({
				display: 'grid',
				gap: 12,
			})}
		>
			<Field label="Licence plate">
				<input className={ui.field} placeholder="VIP777" {...form.getFieldProps({ type: 'text', name: 'plate' })} />
			</Field>
			<Field label="Description">
				<input className={ui.field} placeholder="Executive vehicle" {...form.getFieldProps({ type: 'text', name: 'label' })} />
			</Field>
			{lotsOptions && <Field label="Lot">
				<select className={ui.field} {...lotProps} value={lotProps.value ?? ''}>
					<option value="">Default</option>
					{lotsOptions.map((lot) => (
						<option key={lot.id} value={lot.id}>{lot.name}{lot.description ? ` (${lot.description})` : ''}</option>
					))}
				</select>
			</Field>}
			<label className={css({ color: T.fg, display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 })}>
				<input {...isVipProps} />
				VIP
			</label>
			{isVipProps.checked && <div>
				<div className={css({ color: T.mutedFg, fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', marginBottom: 4, textTransform: 'uppercase' })}>
					VIP contacts
				</div>
				<LoadingFork
					graphqlQuery={contacts}
					renderLoading={() => <div>Loading sanctioned-plate contacts…</div>}
					render={() => (
						<VipContactPicker
							contacts={contacts.contacts || []}
							value={vipContactIdsProps.value || []}
							onChange={vipContactIdsProps.onValueChange}
							requireReachable={false}
							showChannels={false}
							emptyMessage="No sanctioned-plate contacts have been added yet."
						/>
					)}
				/>
			</div>}
			<button type="submit" className={ui.primaryBtn} style={{ width: '100%' }}>Add plate</button>
		</div>
	</form.Form>
)});
